import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, shareReplay, timeout } from 'rxjs';
import { Ticket } from '../models';
import { TicketService } from '../services';

interface CachedPositionRequest {
  stream: Observable<number>;
  /** Instante (ms) até o qual a posição pode ser reaproveitada por outro evento. */
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class PositionUtilService {
  /**
   * Janela em que a posição de um bilhete é reaproveitada entre eventos da
   * mesma rajada. Cada evento de fila faz a lista varrer os N bilhetes do
   * contexto; sem a janela, E eventos na mesma rajada geravam E×N GETs de
   * posição. Medido em produção: dois eventos do mesmo bilhete com 563ms de
   * intervalo — a janela precisa cobrir esse intervalo.
   */
  private static readonly DEDUP_WINDOW_MS = 1000;

  /**
   * Teto para um GET pendurado. Sem ele o cache em voo (`expiresAt = Infinity`)
   * nunca é liberado e um backend/proxy que segure a conexão sem responder
   * imobiliza aquela chave — nenhuma posição nova para o bilhete até o reload.
   */
  private static readonly REQUEST_TIMEOUT_MS = 10000;

  private requests = new Map<string, CachedPositionRequest>();

  constructor(private ticketService: TicketService) {}

  /**
   * Busca a posição atual do bilhete, coalescendo chamadas do mesmo bilhete
   * dentro da janela de dedup.
   *
   * Usa `firstValueFrom` no lugar de `toPromise()` (deprecado no RxJS 7) e
   * propaga o erro do GET. Na versão anterior o subscribe interno não tinha
   * handler de erro: uma falha deixava o Promise pendente para sempre, e o
   * `await` nos handlers de notificação travava o fluxo silenciosamente.
   */
  getNewPosition(ticket: Ticket): Promise<number> {
    return firstValueFrom(this.fetchOnce(ticket));
  }

  private fetchOnce(ticket: Ticket): Observable<number> {
    const key = `${ticket.providerId}:${ticket.id}`;
    const cached = this.requests.get(key);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.stream;
    }

    this.pruneExpired();

    const stream = this.ticketService.getPosition(ticket.providerId, ticket.id).pipe(
      timeout(PositionUtilService.REQUEST_TIMEOUT_MS),
      shareReplay(1)
    );
    const entry: CachedPositionRequest = { stream, expiresAt: Number.POSITIVE_INFINITY };
    this.requests.set(key, entry);

    // Assinatura de aquecimento: dispara o GET e marca quando a resposta chega.
    stream.subscribe({
      next: () => { entry.expiresAt = Date.now() + PositionUtilService.DEDUP_WINDOW_MS; },
      error: () => { entry.expiresAt = 0; }
    });

    return stream;
  }

  /**
   * Descarta entradas vencidas ao inserir uma nova, mantendo o cache limitado
   * ao conjunto vivo. Entradas em voo (`expiresAt` infinito) nunca são podadas.
   */
  private pruneExpired(): void {
    const now = Date.now();
    for (const [key, entry] of this.requests) {
      if (now >= entry.expiresAt) {
        this.requests.delete(key);
      }
    }
  }
}
